import React from "react";
import PersonIcon from "@mui/icons-material/Person";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import {
  calculateAge,
  formatAddressIfPresent,
  formatCountry,
  formatDate,
  hasDisplayValue,
} from "../utils/patientUtils";
import type { SummaryTheme } from "./patientSummaryUi";
import type { PatientTokenInfo } from "./ModalHeader";
import { formatTokenTime } from "./ModalHeader";
import {
  PATIENT_TYPE_STYLES,
  formatPatientTypeLabel,
  resolvePatientTypeLabelFromPatientAndTreatments,
} from "../../appointment/viewappointment/components/appointmentViewShared";

interface ProfileHeaderProps {
  patientData: any;
  theme?: SummaryTheme;
  onEdit?: (id: string, e: React.MouseEvent) => void;
  onDelete?: (id: string, e: React.MouseEvent) => void;
  onSendLab?: (id: string, e: React.MouseEvent) => void;
  canDelete?: boolean;
  onClose: () => void;
  /** Called after delete; defaults to onClose */
  onActionClose?: () => void;
  tokenInfo?: PatientTokenInfo | null;
  patientTypeLabel?: string | null;
  examinationData?: any[];
  /** Optional side panel (e.g. transfer) shown beside patient details */
  aside?: React.ReactNode;
}

function valueClass(
  value: string,
  theme: SummaryTheme,
  accent = false
): string {
  const isDark = theme === "dark";
  if (accent) return isDark ? "text-red-300" : "text-red-600";
  return isDark ? "text-white" : "text-slate-900";
}

function labelClass(theme: SummaryTheme): string {
  const isDark = theme === "dark";
  return `whitespace-nowrap text-[12px] font-bold uppercase tracking-wider ${
    isDark ? "text-white" : "text-slate-900"
  }`;
}

function pairDetails<T>(items: T[]): Array<[T, T | null]> {
  const rows: Array<[T, T | null]> = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push([items[i], items[i + 1] ?? null]);
  }
  return rows;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  patientData,
  theme = "light",
  onEdit,
  onDelete,
  onSendLab,
  canDelete = false,
  onClose,
  onActionClose,
  tokenInfo,
  patientTypeLabel: patientTypeLabelProp,
  examinationData = [],
  aside,
}) => {
  const handleActionClose = onActionClose ?? onClose;
  const isDark = theme === "dark";
  const fullName = `${patientData?.title || ""} ${patientData?.fullName || "Unknown"}`.trim();
  const age = patientData?.dob ? calculateAge(patientData.dob) : null;
  const tokenNo =
    tokenInfo?.tokenNo != null && String(tokenInfo.tokenNo).trim() !== ""
      ? String(tokenInfo.tokenNo)
      : "";
  const tokenTime = formatTokenTime(tokenInfo?.queueGeneratedAt);
  const showToken = Boolean(tokenNo) && Number(tokenNo) > 0;
  const patientTypeLabel =
    patientTypeLabelProp ||
    resolvePatientTypeLabelFromPatientAndTreatments(
      patientData,
      examinationData
    );
  const address = formatAddressIfPresent(patientData?.address);

  const displayOrNo = (value: unknown): string =>
    hasDisplayValue(value) ? String(value) : "No";

  /** Always visible — show "No" when missing */
  const requiredDetails: Array<{ label: string; value: string; accent?: boolean }> = [
    { label: "Email", value: displayOrNo(patientData?.email) },
    { label: "Age", value: age !== null ? `${age} yrs` : "No" },
    { label: "Contact", value: displayOrNo(patientData?.mobileNo) },
    { label: "Gender", value: displayOrNo(patientData?.gender) },
  ];

  const clinicalDetails: Array<{ label: string; value: string }> = [
    { label: "X-Ray", value: displayOrNo(patientData?.xRayNo) },
    { label: "OPG", value: displayOrNo(patientData?.opgNo) },
    { label: "CBCT", value: displayOrNo(patientData?.cbctNo) },
  ];

  const optionalDetails: Array<{ label: string; value: string; accent?: boolean }> = [];
  const push = (label: string, value: unknown, accent = false) => {
    if (hasDisplayValue(value)) {
      optionalDetails.push({ label, value: String(value), accent });
    }
  };

  push("Reg No", patientData?.regNo);
  push("Blood", patientData?.bloodGroup, true);
  push("DOB", patientData?.dob ? formatDate(patientData.dob) : null);
  push("NIC", patientData?.nic);
  push("WhatsApp", patientData?.whatsapp);
  push("Alt. Phone", patientData?.smobileNo);
  push("Registered", patientData?.registerdDate ? formatDate(patientData.registerdDate) : null);
  push("Marital", patientData?.maritalStatus);
  push("Occupation", patientData?.occupation);
  push("Religion", patientData?.religion);
  push("Referred By", patientData?.nameOfIntroduce);
  push("Country", formatCountry(patientData));
  if (address) push("Address", address);

  const details = [...requiredDetails, ...optionalDetails];
  const detailRows = pairDetails(details);

  const actionBtn =
    "inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors";

  return (
    <header
      className={`shrink-0 border-b ${
        isDark ? "border-slate-700 bg-slate-900/95" : "border-slate-200 bg-white/95"
      } backdrop-blur-md`}
    >
      <div className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6 sm:py-3.5">
        <div
          className={`flex flex-col gap-3 lg:gap-4 ${
            aside || showToken ? "lg:flex-row lg:items-stretch" : ""
          }`}
        >
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
            {patientData?.image ? (
              <img
                src={patientData.image}
                alt={fullName}
                className="h-12 w-12 shrink-0 rounded-full object-cover sm:h-14 sm:w-14"
              />
            ) : (
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:h-14 sm:w-14 ${
                  isDark ? "bg-indigo-600 text-white" : "bg-indigo-100 text-indigo-600"
                }`}
              >
                <PersonIcon sx={{ fontSize: 28 }} />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2
                      id="patient-summary-title"
                      className={`truncate text-lg font-bold tracking-tight sm:text-xl ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}
                    >
                      {fullName}
                    </h2>
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold border ${
                        PATIENT_TYPE_STYLES[patientTypeLabel] ||
                        PATIENT_TYPE_STYLES["Existing Patient"]
                      }`}
                    >
                      {formatPatientTypeLabel(patientTypeLabel)}
                    </span>
                    <span
                      className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        isDark
                          ? "bg-slate-800 text-slate-300"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {patientData?.patientType || "Normal"}
                    </span>
                  </div>
                  <p
                    className={`mt-0.5 text-xs font-normal ${
                      isDark ? "text-white" : "text-slate-900"
                    }`}
                  >
                    Patient summary
                  </p>
                </div>

                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                  {onEdit && patientData?._id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(patientData._id, e);
                      }}
                      className={`${actionBtn} ${
                        isDark
                          ? "bg-slate-800 text-slate-200 hover:bg-slate-700"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      <BorderColorIcon sx={{ fontSize: 14 }} />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                  ) : null}

                  {onSendLab && patientData?._id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSendLab(patientData._id, e);
                      }}
                      className={`${actionBtn} bg-gradient-to-r from-primary to-primaryLight text-white hover:opacity-95`}
                    >
                      <ScienceOutlinedIcon sx={{ fontSize: 15 }} />
                      <span className="hidden sm:inline">Send Lab</span>
                    </button>
                  ) : null}

                  {onDelete && patientData?.hasid && canDelete ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (patientData?._id) {
                          onDelete(patientData._id, e);
                          handleActionClose();
                        }
                      }}
                      className={`${actionBtn} ${
                        isDark
                          ? "bg-red-500/15 text-red-300 hover:bg-red-500/25"
                          : "bg-red-50 text-red-600 hover:bg-red-100"
                      }`}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={onClose}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                      isDark
                        ? "bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    aria-label="Close patient summary"
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>
              </div>

              {detailRows.length > 0 || clinicalDetails.length > 0 ? (
                <div
                  className={`mt-3 overflow-x-auto border-t pt-3 space-y-3 ${
                    isDark ? "border-slate-800" : "border-slate-100"
                  }`}
                >
                  {detailRows.length > 0 ? (
                    <table className="w-full min-w-[20rem] border-collapse text-left">
                      <tbody>
                        {detailRows.map(([left, right], rowIndex) => (
                          <tr
                            key={`${left.label}-${rowIndex}`}
                            className={`border-b last:border-b-0 ${
                              isDark ? "border-slate-800/80" : "border-slate-100"
                            }`}
                          >
                            <th
                              scope="row"
                              className={`w-[18%] py-1.5 pr-2 align-top ${labelClass(theme)}`}
                            >
                              {left.label} :
                            </th>
                            <td
                              className={`w-[32%] py-1.5 pr-4 align-top text-sm font-normal ${valueClass(
                                left.value,
                                theme,
                                left.accent
                              )}`}
                            >
                              <span className="break-words">{left.value}</span>
                            </td>
                            {right ? (
                              <>
                                <th
                                  scope="row"
                                  className={`w-[18%] py-1.5 pr-2 align-top ${labelClass(theme)}`}
                                >
                                  {right.label} :
                                </th>
                                <td
                                  className={`w-[32%] py-1.5 align-top text-sm font-normal ${valueClass(
                                    right.value,
                                    theme,
                                    right.accent
                                  )}`}
                                >
                                  <span className="break-words">{right.value}</span>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="w-[18%]" />
                                <td className="w-[32%]" />
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}

                  <div>
                    <p
                      className={`mb-1.5 text-xs font-bold ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}
                    >
                      Clinical record
                    </p>
                    <table className="w-full min-w-[20rem] border-collapse text-left">
                      <tbody>
                        <tr>
                          {clinicalDetails.map((item, index) => (
                            <React.Fragment key={item.label}>
                              <th
                                scope="row"
                                className={`py-1.5 pr-0 align-middle w-[80px] ${labelClass(theme)}`}
                              >
                                {item.label} :
                              </th>
                              <td
                                className={`py-1.5 align-left text-sm font-normal ${
                                  index < clinicalDetails.length - 1 ? "pr-4" : ""
                                } ${valueClass(item.value, theme)}`}
                              >
                                <span className="break-words">{item.value}</span>
                              </td>
                            </React.Fragment>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {aside || showToken ? (
            <aside
              className={`flex w-full shrink-0 flex-col gap-3 lg:w-[22rem] xl:w-[24rem] lg:border-l lg:pl-4 ${
                isDark ? "border-slate-700" : "border-slate-200"
              }`}
            >
              {showToken ? (
                <div
                  className={`flex items-center gap-3 ${
                    aside
                      ? `border-b pb-3 ${isDark ? "border-slate-700" : "border-slate-200"}`
                      : ""
                  }`}
                  title={tokenTime ? `Token generated at ${tokenTime}` : "Token number"}
                >
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                      isDark
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-emerald-50 text-emerald-600"
                    }`}
                  >
                    <ConfirmationNumberOutlinedIcon sx={{ fontSize: 26 }} />
                  </span>
                  <div className="min-w-0">
                    <p className={labelClass(theme)}>
                      Token
                    </p>
                    <div
                      className={`flex items-baseline gap-2 leading-none ${
                        isDark ? "text-emerald-300" : "text-emerald-700"
                      }`}
                    >
                      <span className="text-4xl font-bold tabular-nums tracking-tight sm:text-5xl">
                        {tokenNo}
                      </span>
                      {tokenTime ? (
                        <span className="text-sm font-medium opacity-80">{tokenTime}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
              {aside}
            </aside>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default ProfileHeader;
